import React, { useReducer } from "react";
import { useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import { Box, Grid } from "@mui/material";
import moment from "moment";
import dayjs from "dayjs";
import { InputText } from "../../uiControls/inputText";
import { InputDate } from "../../uiControls/inputDate";
import { InputTime } from "../../uiControls/inputTime";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { MessageDisplay } from "../../common/messageDisplay";
import { SaveCancel } from "../../uiControls/saveCancel";
import { StatusControl } from "../../uiControls/statusControl";
import * as utils from "../../common/utils";
import "../../uiControls/commonStyles.css";

const initialSiteStandardIsValid = {
  facilityKey: "",
  name: "",
  startDate: "",
  endDate: "",
  shift1Start: "",
  shift2Start: "",
  shift3Start: "",
  rtacustomer: "",
  dtccustomer: "",
  rtccustomer: "",
  rtacompany: "",
  dtccompany: "",
  rtccompany: "",
  tpph: "",
  lunchTime: "",
  breakTime: "",
  message: "",
  type: "",
};

export const SiteStandardUpdate = (props) => {
  const {
    setUpdateOpen,
    siteStandardEdit,
    setSiteStandardEdit,
    siteStandardAttributes,
  } = props;
  const messageDispatch = useDispatch();
  const { sendRequest: updateSiteStandard } = useHttpRequests();

  const [siteStandardDetails, setSiteStandardDetails] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    siteStandardAttributes
  );

  const [isValid, setIsValid] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialSiteStandardIsValid
  );

  const updateSiteStandardDetailsValue = (_key, value) => {
    setSiteStandardDetails({ [_key]: value });
    if (value) {
      updateErrorMessage([_key], "", "");
    }
  };

  const updateErrorMessage = (_key, header, errMsg) => {
    setIsValid({ [_key]: errMsg + header });
  };

  const onChangeHandler = (e, controlName) => {
    updateSiteStandardDetailsValue(
      controlName,
      e.target.type === "checkbox" ? e.target.checked : e.target.value
    );
  };

  const cancelSiteStandardHandler = () => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setSiteStandardEdit(false);
    setUpdateOpen(false);
  };

  const onDateChangeHandler = (e, controlName) => {
    updateSiteStandardDetailsValue(controlName, e);
  };

  const validateSiteStandardDetails = () => {
    setIsValid(initialSiteStandardIsValid);

    var shift1StartTime = "";
    var shift2StartTime = "";
    var shift3StartTime = "";

    if (dayjs.isDayjs(siteStandardDetails.shift1Start)) {
      shift1StartTime = siteStandardDetails.shift1Start.format("HH:mm");
    }
    else {
       const timeStr = String(siteStandardDetails.shift1Start).trim();
        if (timeStr.includes('T')) {
          shift1StartTime = moment.utc(siteStandardDetails.shift1Start).format("HH:mm");
        }
    }

    if (dayjs.isDayjs(siteStandardDetails.shift2Start)) {
      shift2StartTime = siteStandardDetails.shift2Start.format("HH:mm");
    }
    else {
       const timeStr = String(siteStandardDetails.shift2Start).trim();
        if (timeStr.includes('T')) {
          shift2StartTime = moment.utc(siteStandardDetails.shift2Start).format("HH:mm");
        }
    }

    if (dayjs.isDayjs(siteStandardDetails.shift3Start)) {
      shift3StartTime = siteStandardDetails.shift3Start.format("HH:mm");
    }
    else {
       const timeStr = String(siteStandardDetails.shift3Start).trim();
        if (timeStr.includes('T')) {
          shift3StartTime = moment.utc(siteStandardDetails.shift3Start).format("HH:mm");
        }
    }

    var errorSummary = "";

    if (
      !siteStandardDetails.facilityKey ||
      siteStandardDetails.facilityKey <= 0
    ) {
      updateErrorMessage("facilityKey", "Facility", "Select ");
      errorSummary += "Select facility. \n";
    }

    if (!siteStandardDetails.name) {
      updateErrorMessage("name", "Site standard name", "Enter ");
      errorSummary += "Enter site standard name \n";
    } else if (!utils.isValidCharacters(siteStandardDetails.name)) {
      updateErrorMessage(
        "name",
        "Site standard name",
        " has restricted characters"
      );
      errorSummary += "Site standard name has restricted characters \n";
    }

    if (!siteStandardDetails.startDate) {
      updateErrorMessage("startDate", "Start Date", "Enter ");
      errorSummary += "Enter start date \n";
    }

    if (!siteStandardDetails.endDate) {
      updateErrorMessage("endDate", "End Date", "Enter ");
      errorSummary += "Enter end date \n";
    }

    if (!siteStandardDetails.shift1Start) {
      updateErrorMessage("shift1Start", "Shift 1 start time", "Enter ");
      errorSummary += "Enter shift 1 start time \n";
    } else if (
      siteStandardDetails.shift1Start &&
      !utils.isValidTime24Hour(shift1StartTime)
    ) {
      updateErrorMessage("shift1Start", "Shift 1 start time", "Enter ");
      errorSummary += "Shift 1 start time is invalid. \n";
    }

    if (!siteStandardDetails.shift2Start) {
      updateErrorMessage("shift2Start", "Shift 2 start time", "Enter ");
      errorSummary += "Enter shift 2 start time \n";
    } else if (
      siteStandardDetails.shift2Start &&
      !utils.isValidTime24Hour(shift2StartTime)
    ) {
      updateErrorMessage("shift2Start", "Shift 2 start time", "Enter ");
      errorSummary += "Shift 2 start time is invalid. \n";
    }

    if (!siteStandardDetails.shift3Start) {
      updateErrorMessage("shift3Start", "Shift 3 start time", "Enter ");
      errorSummary += "Enter shift 3 start time \n";
    } else if (
      siteStandardDetails.shift3Start &&
      !utils.isValidTime24Hour(shift3StartTime)
    ) {
      updateErrorMessage("shift3Start", "Shift 3 start time", "Enter ");
      errorSummary += "Shift 3 start time is invalid. \n";
    }

    if (
      isNaN(siteStandardDetails.rtacustomer) ||
      siteStandardDetails.rtacustomer <= 0
    ) {
      updateErrorMessage("rtacustomer", "RTA Customer", "Enter ");
      errorSummary += "Enter RTA Customer \n";
    }

    if (
      isNaN(siteStandardDetails.dtccustomer) ||
      siteStandardDetails.dtccustomer <= 0
    ) {
      updateErrorMessage("dtccustomer", "DTC Customer", "Enter ");
      errorSummary += "Enter DTC Customer \n";
    }

    if (
      isNaN(siteStandardDetails.rtccustomer) ||
      siteStandardDetails.rtccustomer <= 0
    ) {
      updateErrorMessage("rtccustomer", "RTC Customer", "Enter ");
      errorSummary += "Enter RTC Customer \n";
    }

    if (
      isNaN(siteStandardDetails.rtacompany) ||
      siteStandardDetails.rtacompany <= 0
    ) {
      updateErrorMessage("rtacompany", "RTA Company", "Enter ");
      errorSummary += "Enter RTA Company \n";
    }

    if (
      isNaN(siteStandardDetails.dtccompany) ||
      siteStandardDetails.dtccompany <= 0
    ) {
      updateErrorMessage("dtccompany", "DTC Company", "Enter ");
      errorSummary += "Enter DTC Company \n";
    }

    if (
      isNaN(siteStandardDetails.rtccompany) ||
      siteStandardDetails.rtccompany <= 0
    ) {
      updateErrorMessage("rtccompany", "RTC Company", "Enter ");
      errorSummary += "Enter RTC Company \n";
    }

    if (isNaN(siteStandardDetails.tpph) || siteStandardDetails.tpph <= 0) {
      updateErrorMessage("tpph", "TPPH", "Enter ");
      errorSummary += "Enter TPPH \n";
    }

    if (
      isNaN(siteStandardDetails.breakTime) ||
      siteStandardDetails.breakTime <= 0
    ) {
      updateErrorMessage("breakTime", "Break Time", "Enter ");
      errorSummary += "Enter break time \n";
    }

    if (
      isNaN(siteStandardDetails.lunchTime) ||
      siteStandardDetails.lunchTime <= 0
    ) {
      updateErrorMessage("lunchTime", "Lunch Time", "Enter ");
      errorSummary += "Enter lunch time \n";
    }

    if (errorSummary !== "") {
      messageDispatch(
        setMessageHandler({ type: "error", message: errorSummary })
      );
    } else {
      const dataObj = { ...siteStandardDetails };;
      dataObj.shift1Start = shift1StartTime;
      dataObj.shift2Start = shift2StartTime;
      dataObj.shift3Start = shift3StartTime;

      const getUpdateSiteStandardResponse = (resObj) => {
        if (
          !resObj ||
          !resObj["isSuccess"] ||
          (resObj["validationErrors"] !== undefined &&
            resObj["validationErrors"]?.length > 0)
        ) {
          messageDispatch(
            setMessageHandler({
              type: "error",
              message:
                (resObj["message"] ? resObj["message"] : "") +
                (resObj["validationErrors"]?.length > 0
                  ? "\n" + resObj["validationErrors"]?.join("\n")
                  : ""),
            })
          );
          return;
        }
        messageDispatch(
          setMessageHandler({
            type: "success",
            message: "Site standard updated successfully!",
          })
        );
        setTimeout(() => {
          cancelSiteStandardHandler();
        }, 2000);
      };

      if (siteStandardEdit) {
        updateSiteStandard(
          {
            url: "SiteStandards/" + dataObj["siteStandardKey"],
            method: "PUT",
            body: dataObj,
          },
          getUpdateSiteStandardResponse
        );
      } else {
        delete dataObj["siteStandardKey"];
        updateSiteStandard(
          { url: "SiteStandards", body: dataObj },
          getUpdateSiteStandardResponse
        );
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          overflow: "auto",
          padding: "0px",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <MessageDisplay />
          <InputText
            type="text"
            label="Name"
            id={"siteStandardDetails.name"}
            value={siteStandardDetails.name}
            onChange={(e) => onChangeHandler(e, "name")}
            isRequired={true}
            isError={isValid["name"] !== ""}
            charLength={50}
          />
          <Grid container sx={{ display: "flex", gap: "15px" }}>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputDate
                label="Start Date"
                id={"siteStandardDetails.startDate"}
                value={siteStandardDetails.startDate}
                onChange={(e) => onDateChangeHandler(e, "startDate")}
                isRequired={true}
                isError={isValid["startDate"] !== ""}
                displayType={"grid"}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputDate
                label="End Date"
                id={"siteStandardDetails.endDate"}
                value={siteStandardDetails.endDate}
                onChange={(e) => onDateChangeHandler(e, "endDate")}
                isRequired={true}
                isError={isValid["endDate"] !== ""}
                displayType={"grid"}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}></Grid>
          </Grid>
          <Grid container sx={{ display: "flex", gap: "15px" }}>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputTime
                label="Shift 1 Start"
                id={"siteStandardDetails.shift1Start"}
                selectedValue={siteStandardDetails.shift1Start}
                onChange={(e) => onDateChangeHandler(e, "shift1Start")}
                isRequired={true}
                isError={isValid["shift1Start"] !== ""}
                hour12={true}
                stepMinutes={15}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputTime
                label="Shift 2 Start"
                id={"siteStandardDetails.shift2Start"}
                selectedValue={siteStandardDetails.shift2Start}
                onChange={(e) => onDateChangeHandler(e, "shift2Start")}
                isRequired={true}
                isError={isValid["shift2Start"] !== ""}
                hour12={true}
                stepMinutes={15}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputTime
                label="Shift 3 Start"
                id={"siteStandardDetails.shift3Start"}
                selectedValue={siteStandardDetails.shift3Start}
                onChange={(e) => onDateChangeHandler(e, "shift3Start")}
                isRequired={true}
                isError={isValid["shift3Start"] !== ""}
                hour12={true}
                stepMinutes={15}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ display: "flex", gap: "15px" }}>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="RTA Customer"
                id={"siteStandardDetails.rtacustomer"}
                value={siteStandardDetails.rtacustomer}
                onChange={(e) => onChangeHandler(e, "rtacustomer")}
                isRequired={true}
                isError={isValid["rtacustomer"] !== ""}
                charLength={5}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="DTC Customer"
                id={"siteStandardDetails.dtccustomer"}
                value={siteStandardDetails.dtccustomer}
                onChange={(e) => onChangeHandler(e, "dtccustomer")}
                isRequired={true}
                isError={isValid["dtccustomer"] !== ""}
                charLength={5}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="RTC Customer"
                id={"siteStandardDetails.rtccustomer"}
                value={siteStandardDetails.rtccustomer}
                onChange={(e) => onChangeHandler(e, "rtccustomer")}
                isRequired={true}
                isError={isValid["rtccustomer"] !== ""}
                charLength={5}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ display: "flex", gap: "15px" }}>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="RTA Company"
                id={"siteStandardDetails.rtacompany"}
                value={siteStandardDetails.rtacompany}
                onChange={(e) => onChangeHandler(e, "rtacompany")}
                isRequired={true}
                isError={isValid["rtacompany"] !== ""}
                charLength={5}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="DTC Company"
                id={"siteStandardDetails.dtccompany"}
                value={siteStandardDetails.dtccompany}
                onChange={(e) => onChangeHandler(e, "dtccompany")}
                isRequired={true}
                isError={isValid["dtccompany"] !== ""}
                charLength={5}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="RTC Company"
                id={"siteStandardDetails.rtccompany"}
                value={siteStandardDetails.rtccompany}
                onChange={(e) => onChangeHandler(e, "rtccompany")}
                isRequired={true}
                isError={isValid["rtccompany"] !== ""}
                charLength={5}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ display: "flex", gap: "15px" }}>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="TPPH"
                id={"siteStandardDetails.tpph"}
                value={siteStandardDetails.tpph}
                onChange={(e) => onChangeHandler(e, "tpph")}
                isRequired={true}
                isError={isValid["tpph"] !== ""}
                charLength={5}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="Lunch Time"
                id={"siteStandardDetails.lunchTime"}
                value={siteStandardDetails.lunchTime}
                onChange={(e) => onChangeHandler(e, "lunchTime")}
                isRequired={true}
                isError={isValid["lunchTime"] !== ""}
                charLength={3}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: "flex" }}></Grid>
            <Grid item xs={3} sx={{ display: "flex" }}>
              <InputText
                type="number"
                label="Break Time"
                id={"siteStandardDetails.breakTime"}
                value={siteStandardDetails.breakTime}
                onChange={(e) => onChangeHandler(e, "breakTime")}
                isRequired={true}
                isError={isValid["breakTime"] !== ""}
                charLength={3}
              />
            </Grid>
          </Grid>
          <StatusControl
            label="Active"
            isRequired={false}
            fieldName={"active"}
            active={siteStandardDetails.active}
            updateStatusValue={updateSiteStandardDetailsValue}
          />
          <SaveCancel
            cancelCall={() => cancelSiteStandardHandler()}
            saveCall={() => validateSiteStandardDetails()}
          />
        </div>
      </Box>
    </>
  );
};
